/* eslint-disable no-use-before-define */
import { replicateRxCollection, RxReplicationState } from 'rxdb/plugins/replication';
import _ from 'lodash';
import { RxError, RxTypeError } from 'rxdb';
import { Subject } from 'rxjs';
import buildPath from '@/tsClient/utilities/buildPath';
import formsBackendClient, { ApiDefinition } from '@/tsClient/servers/noodleQuestionnaires';
import { getNoodleDb } from './rxdb.noodle';
import { RxQuestionnaireTemplateDocType } from './schemas/questionnaireTemplate.schema';
import { RxNoodleDBCollectionsKeys } from './rxdb.types';
import { RxQuestionnaireFieldTemplateDocType } from './schemas/questionnaireFieldTemplate.schema';
import { REPLICATION_INFO_STATUS, RX_LOCAL_REPLICATION_INFO } from './rxdb.constants';
import { CompletionPromiseReturn, getMaxQuestionnairesPerCreator, getPromiseAndResolver, rxdbError, rxdbLog, wait } from './rxdb.tools';
import { RxQuestionnaireResponseDocType } from './schemas/questionnaireResponse.schema';

type CheckpointType = {
  id: string;
  updatedAt: string;
};

type UnkownReplicationState = RxReplicationState<unknown, CheckpointType>;

export type ReplicationIdentifier = string;

/**
 * Type representing the response from the replication fetch API.
 * @template T - The document type being replicated
 * @property documents - Array of documents with deletion status and updatedAt timestamp
 * @property checkpoint - The checkpoint of the replication
 */
type PullReplicationFetchResponse<T> = {
  // We need explicitly define the type of the documents array at least with
  // the fields that are REQUIRED for the replication to work.
  documents: (T & { _deleted: boolean })[];
  checkpoint: CheckpointType;
};

interface ReplicationStateInfo {
  status: REPLICATION_INFO_STATUS;
  lastError: Error | null;
  isPausing: boolean;
}

export interface ReplicationData {
  addingReplicationPromise: null | CompletionPromiseReturn;
  state: UnkownReplicationState | null;
  info: ReplicationStateInfo;
  onFinishPull: Subject<void>;
  onPullHappened: Subject<PullReplicationFetchResponse<unknown>>;
}

interface ReplicationsData {
  [key: ReplicationIdentifier]: ReplicationData | undefined;
}

let initialReplicationDate: string | null = null;
let globalCreatorId: string | null = null;

export const replicationsData$ = new Subject<ReplicationsData>();
const initialReplicationDatePromise: CompletionPromiseReturn = getPromiseAndResolver();

let replicationsData: ReplicationsData = {};
let prioritizedReplications: Set<ReplicationIdentifier> = new Set();
let pausedReplications: Set<ReplicationIdentifier> = new Set();

const QUESTIONNAIRE_FIELD_TEMPLATES_PREFIX = 'questionnaireFieldTemplate';
const QUESTIONNAIRE_RESPONSES_PREFIX = 'questionnaireResponse';
/**
 * Starts the replication process for a specific creator.
 */
async function startReplications({ creatorId }: { creatorId: string }): Promise<(() => void) | null> {
  globalCreatorId = creatorId;

  // It's important to call this function first as it sets the initial replication date
  // Which is then used by the replication process to only fetch documents that have been
  // Deleted since this date (in that way we avoid fetching useless deleted documents).
  const successfullySetLocalReplicationInfo = await setLocalReplicationInfo();
  rxdbLog('local replication info set', { successfullySetLocalReplicationInfo });
  if (!successfullySetLocalReplicationInfo) return null;

  const { templatesIds, upgradedQuestionnairesIds } = await getOrderOfReplication(creatorId);
  rxdbLog('order of replication fetched', { templatesIds, upgradedQuestionnairesIds });

  await replicateQuestionnaireTemplates({ creatorId });
  rxdbLog('questionnaire templates replicated');

  await checkUnusedTemplates(templatesIds);
  rxdbLog('unused templates checked');

  await checkUnUsedQuestionnairesResponses(upgradedQuestionnairesIds);
  rxdbLog('unused questionnaires responses checked');

  await checkNumberOfQuestionnairesPerCreator();
  rxdbLog('number of questionnaires per creator checked');

  return () => {
    cancelAllReplications();
    initialReplicationDate = null;
    globalCreatorId = null;
    replicationsData = {};
    prioritizedReplications = new Set();
    pausedReplications = new Set();
  };
}

/**
 * Sets the local replication info for the database.
 *
 * @returns {boolean} - True if the local replication info was set successfully or correctly fetched, false otherwise.
 */
async function setLocalReplicationInfo(): Promise<boolean> {
  try {
    const db = await getNoodleDb();

    const { KEY, INITIAL_DATE_KEY } = RX_LOCAL_REPLICATION_INFO;

    const localReplicationInfo = await db.getLocal(KEY);

    if (localReplicationInfo) {
      initialReplicationDate = localReplicationInfo.get(INITIAL_DATE_KEY);
      return true;
    }

    initialReplicationDate = new Date().toISOString();

    await db.upsertLocal(KEY, {
      [INITIAL_DATE_KEY]: initialReplicationDate,
    });

    return true;
  } catch (error) {
    rxdbError('setLocalReplicationInfo - Error setting initial replication date', error);
    return false;
  } finally {
    initialReplicationDatePromise.resolver();
  }
}

type ThisOrderOfReplicationApiResponse = ApiDefinition['/questionnaires/{creatorId}/order-for-replication']['GET']['response'];

async function getOrderOfReplication(creatorId: string): Promise<ThisOrderOfReplicationApiResponse> {
  await initialReplicationDatePromise.promise;

  if (!initialReplicationDate) {
    throw new Error('Initial replication date not set');
  }

  const path = buildPath<ApiDefinition['/questionnaires/{creatorId}/order-for-replication']['GET']>(
    '/questionnaires/{creatorId}/order-for-replication',
    { creatorId },
  );
  const response = await formsBackendClient.get<ThisOrderOfReplicationApiResponse>(path, {
    params: { upgradedFromDate: initialReplicationDate },
  });
  return response.data;
}

/**
 * Retrieves the questionnaire template replication identifier
 * @returns {ReplicationIdentifier} - The replication identifier.
 */
export function getQuestionnaireTemplateReplicationIdentifier(): ReplicationIdentifier {
  return 'questionnaireTemplate';
}

/**
 * Retrieves the questionnaire field template replication identifier according to the template ID.
 * @param {string} templateId - The ID of the questionnaire template.
 * @returns {ReplicationIdentifier} - The replication identifier.
 */
export function getQuestionnaireFieldTemplateReplicationIdentifier(templateId: string): ReplicationIdentifier {
  return `${QUESTIONNAIRE_FIELD_TEMPLATES_PREFIX}-${templateId}`;
}

/**
 * Retrieves the questionnaire response replication identifier according to the questionnaire ID.
 * @param {string} questionnaireId - The ID of the questionnaire.
 * @returns {ReplicationIdentifier} - The replication identifier.
 */
export function getQuestionnaireResponseReplicationIdentifier(questionnaireId: string): ReplicationIdentifier {
  return `${QUESTIONNAIRE_RESPONSES_PREFIX}-${questionnaireId}`;
}

export async function getReplicationDataByIdentifier(
  identifier: ReplicationIdentifier,
  { waitUntilReplicationIsAdded = false }: { waitUntilReplicationIsAdded?: boolean } = {},
): Promise<ReplicationData | null> {
  let replicationData = replicationsData[identifier];

  if (!replicationData && waitUntilReplicationIsAdded) {
    await new Promise<void>(resolve => {
      const subscription = replicationsData$.subscribe(updatedReplicationsData => {
        if (updatedReplicationsData[identifier]) {
          replicationData = updatedReplicationsData[identifier];
          subscription.unsubscribe();
          resolve();
        }
      });
    });
  }

  if (!replicationData) return null;

  // Wait for the replication to be added if it's not already added
  if (replicationData.addingReplicationPromise) {
    await replicationData.addingReplicationPromise.promise;
  }

  return replicationData;
}

/**
 * Replicates questionnaire templates for a specific creator.
 */
function replicateQuestionnaireTemplates({
  creatorId,
}: {
  creatorId: string;
}): Promise<RxReplicationState<RxQuestionnaireTemplateDocType, CheckpointType>> {
  rxdbLog('replicateQuestionnaireTemplates - replicating questionnaire templates for creator', { creatorId });
  return replicateCollection<RxQuestionnaireTemplateDocType>({
    collection: 'questionnaireTemplate',
    limit: 200,
    path: buildPath('/questionnaire-templates/{creatorId}/pull/questionnaire-templates', { creatorId }),
    replicationIdentifier: getQuestionnaireTemplateReplicationIdentifier(),
  });
}

/**
 * Creates a replication for a specific questionnaire field template.
 * @param {string} templateId - The ID of the questionnaire template.
 * @param {string} creatorId - The ID of the creator.
 * @returns {Promise<RxReplicationState<RxQuestionnaireFieldTemplateDocType, CheckpointType>>} - The replication state.
 */
function createQuestionnaireFieldTemplateReplication(
  templateId: string,
  creatorId: string,
): Promise<RxReplicationState<RxQuestionnaireFieldTemplateDocType, CheckpointType>> {
  return replicateCollection<RxQuestionnaireFieldTemplateDocType>({
    autoStart: false,
    collection: 'questionnaireFieldTemplate',
    limit: 100_000,
    path: buildPath('/questionnaire-templates/{creatorId}/{questionnaireTemplateId}/pull/field-templates', {
      creatorId,
      questionnaireTemplateId: templateId,
    }),
    preventStoreAssumedMasterDocs: true,
    replicationIdentifier: getQuestionnaireFieldTemplateReplicationIdentifier(templateId),
  });
}

/**
 * Creates a replication for a specific questionnaire response.
 * @param {string} questionnaireId - The ID of the questionnaire.
 * @returns {Promise<RxReplicationState<RxQuestionnaireResponseDocType, CheckpointType>>} - The replication state.
 */
function createQuestionnaireResponsesReplication(
  questionnaireId: string,
): Promise<RxReplicationState<RxQuestionnaireResponseDocType, CheckpointType>> {
  return replicateCollection<RxQuestionnaireResponseDocType>({
    autoStart: false,
    collection: 'questionnaireResponse',
    limit: 100_000,
    path: buildPath('/questionnaires/{questionnaireId}/pull/responses', { questionnaireId }),
    replicationIdentifier: getQuestionnaireResponseReplicationIdentifier(questionnaireId),
  });
}

/**
 * Initializes a replication for a specific collection.
 * @param {Object} options - The options object.
 * @param {RxNoodleDBCollectionsKeys} options.collection - The collection to replicate.
 * @param {string} options.path - The path to the collection endpoint.
 * @param {number} [options.limit=100] - The number of documents to pull in each batch.
 * @param {ReplicationIdentifier} [options.replicationIdentifier] - The identifier for the replication.
 * @returns {Promise<RxReplicationState<T, CheckpointType>>} - The replication state.
 */
async function replicateCollection<T>({
  collection,
  path,
  limit,
  replicationIdentifier,
  autoStart = true,
  preventStoreAssumedMasterDocs = false,
}: {
  collection: RxNoodleDBCollectionsKeys;
  path: string;
  limit: number;
  replicationIdentifier?: ReplicationIdentifier;
  autoStart?: boolean;
  preventStoreAssumedMasterDocs?: boolean;
}): Promise<RxReplicationState<T, CheckpointType>> {
  await initialReplicationDatePromise.promise;

  if (!initialReplicationDate) {
    throw new Error('Initial replication date not set');
  }

  const db = await getNoodleDb();
  const identifier = replicationIdentifier || collection;
  const existingReplicationData = await getReplicationDataByIdentifier(identifier);

  let bulkWriteOverrided = false;

  if (existingReplicationData) {
    rxdbLog(`replicateCollection - replication already exists for identifier: ${identifier}`);
    return existingReplicationData.state as RxReplicationState<T, CheckpointType>;
  }

  const replicationData = createReplicationData(identifier);
  const replicationState = await replicateRxCollection<T, CheckpointType>({
    autoStart,
    collection: db[collection],
    pull: { batchSize: limit, handler: pullHandler },
    replicationIdentifier: identifier,
    waitForLeadership: false,
  });

  addReplicationStateToData(replicationState as UnkownReplicationState);

  return replicationState;

  /**
   * Executes the request to the backend to fetch the documents to be replicated (a.k.a the documents to be stored in the local database).
   * @param {CheckpointType | undefined} checkpointOrNull - The checkpoint of the replication.
   * @param {number} batchSize - The number of documents to pull in each batch.
   * @returns {Promise<PullReplicationFetchResponse<T>>} - The response from the pull replication.
   */
  async function pullHandler(checkpointOrNull: CheckpointType | undefined, batchSize: number): Promise<PullReplicationFetchResponse<T>> {
    if (preventStoreAssumedMasterDocs) {
      overrideBulkWrite();
    }

    const data = await formsBackendClient.get<PullReplicationFetchResponse<T>>(path, {
      params: {
        deletedFromDate: initialReplicationDate,
        id: checkpointOrNull?.id,
        limit: batchSize,
        updatedAt: checkpointOrNull?.updatedAt,
      },
    });

    // If the number of documents returned is less than the batch size, the replication has finished
    if (data.data.documents.length < batchSize) {
      replicationData.onFinishPull.next();
      handlePrioritizedReplicationsFinished(identifier);
      markLastReplicationDateForIdentifier(identifier);
    }

    replicationData.onPullHappened.next(data.data);

    return data.data;
  }

  async function overrideBulkWrite(): Promise<void> {
    if (bulkWriteOverrided || !replicationState.metaInstance) return;

    bulkWriteOverrided = true;

    const originalBulkWrite = replicationState.metaInstance.bulkWrite;

    replicationState.metaInstance.bulkWrite = async (docs, context) =>
      originalBulkWrite(
        docs.map(doc => ({
          ...doc,
          document: {
            ...doc.document,
            docData: {},
          },
        })),
        context,
      );
  }
}

/**
 * Creates a replication data object for a specific identifier.
 * @param {ReplicationIdentifier} identifier - The identifier of the replication.
 * @returns {ReplicationData} - The replication data object.
 */
function createReplicationData(identifier: ReplicationIdentifier): ReplicationData {
  const { IDLE } = REPLICATION_INFO_STATUS;

  const replicationData: ReplicationData = {
    addingReplicationPromise: getPromiseAndResolver(),
    info: {
      isPausing: false,
      lastError: null,
      status: IDLE,
    },
    onFinishPull: new Subject<void>(),
    onPullHappened: new Subject<PullReplicationFetchResponse<unknown>>(),
    state: null,
  };

  replicationsData[identifier] = replicationData;

  triggerReplicationsDataChange();

  return replicationData;
}

/**
 * Removes the replication data for a specific identifier.
 * @param {ReplicationIdentifier} identifier - The identifier of the replication to remove.
 */
function removeReplicationData(identifier: ReplicationIdentifier): void {
  if (!replicationsData[identifier]) return;

  delete replicationsData[identifier];
  triggerReplicationsDataChange();
}

/**
 * Adds a replication state to the replication data object.
 * Subscribes to the replication state's events.
 * It also resolves the adding replication promise if it exists.
 * @param {UnkownReplicationState} replicationState - The replication state to add.
 * @returns {ReplicationData | null} - The replication data object or null if the identifier does not exist.
 */
function addReplicationStateToData(replicationState: UnkownReplicationState): ReplicationData | null {
  const identifier = replicationState.replicationIdentifier;
  const { ACTIVE, CANCELED, COMPLETED, ERROR, IDLE, PAUSED } = REPLICATION_INFO_STATUS;

  if (!replicationsData[identifier]) {
    rxdbError(`addReplicationStateToData - can not add replication state to data as it does not exist: ${identifier}`);
    return null;
  }

  const update = (data: unknown): void => {
    if (!replicationsData[identifier]) return;
    replicationsData[identifier] = _.merge(replicationsData[identifier], data);
    triggerReplicationsDataChange();
  };

  if (replicationsData[identifier]?.addingReplicationPromise) {
    replicationsData[identifier]?.addingReplicationPromise?.resolver();
  }

  update({
    addingReplicationPromise: null,
    info: { ...replicationsData[identifier]?.info, status: replicationState.wasStarted ? ACTIVE : IDLE },
    state: replicationState,
  });

  // Subscribe to error state
  replicationState.error$.subscribe(error => {
    update({ info: { lastError: error, status: ERROR } });
    handleReplicationError(error);
  });

  // Subscribe to active state
  replicationState.active$.subscribe(active => {
    update({ info: { isPausing: false, status: getInfoStatus(active) } });
  });

  // Subscribe to canceled state
  replicationState.canceled$.subscribe(canceled => {
    if (!canceled) return;
    update({ info: { status: CANCELED } });
  });

  return replicationsData[identifier] || null;

  function getInfoStatus(active: boolean): REPLICATION_INFO_STATUS {
    if (active) return ACTIVE;
    if (replicationState.isPaused()) return PAUSED;
    if (replicationState.wasStarted) return COMPLETED;
    return IDLE;
  }

  function handleReplicationError(error: RxError | RxTypeError): void {
    const errors = error?.parameters?.errors;
    const firstErrorMessage = errors?.[0]?.message;

    if (firstErrorMessage?.includes('401')) cancelReplication('authentication error');
    else if (firstErrorMessage?.includes('404')) cancelReplication('404 error');
    else if (firstErrorMessage?.includes('Network Error')) rxdbError(`${replicationState.replicationIdentifier} error due to network error`);
    else rxdbError(`${replicationState.replicationIdentifier} error`, error);

    function cancelReplication(dueReason: string): void {
      rxdbLog(`${replicationState.replicationIdentifier} cancelling due to ${dueReason}.`);
      replicationState.cancel();
    }
  }
}

function triggerReplicationsDataChange(): void {
  replicationsData$.next(replicationsData);
}

async function tryToCreateReplication(identifier: ReplicationIdentifier): Promise<null | UnkownReplicationState> {
  const isQuestionnaireFieldTemplateReplication = identifier.startsWith(QUESTIONNAIRE_FIELD_TEMPLATES_PREFIX);
  const isQuestionnaireResponsesReplication = identifier.startsWith(QUESTIONNAIRE_RESPONSES_PREFIX);

  if (!isQuestionnaireFieldTemplateReplication && !isQuestionnaireResponsesReplication) return null;

  if (!globalCreatorId) return null;

  const id = identifier.split('-')[1];

  if (isQuestionnaireFieldTemplateReplication) {
    return createQuestionnaireFieldTemplateReplication(id, globalCreatorId) as unknown as UnkownReplicationState;
  }

  if (isQuestionnaireResponsesReplication) {
    return createQuestionnaireResponsesReplication(id) as unknown as UnkownReplicationState;
  }

  return null;
}

/**
 * Starts or resyncs a replication by identifier.
 * If there are prioritized replications and this replication is not prioritized, it will be ignored.
 * @param {ReplicationIdentifier} identifier - The identifier of the replication to start or resync.
 * @param {boolean} [options.waitToFinish=false] - Whether to wait for the replication to finish.
 * @returns {Promise<boolean>} - A promise that resolves to true if the replication was started or resynced, false otherwise.
 */
export async function startOrResyncReplication(
  identifier: ReplicationIdentifier,
  { waitToFinish = false }: { waitToFinish?: boolean } = {},
): Promise<boolean> {
  // This is to ensure that the database is initialized
  await getNoodleDb();

  let replicationData = await getReplicationDataByIdentifier(identifier);

  if (replicationData?.info?.status === REPLICATION_INFO_STATUS.ACTIVE) return false;

  if (!replicationData?.state) {
    const replicationState = await tryToCreateReplication(identifier);
    if (!replicationState) return false;

    replicationData = await getReplicationDataByIdentifier(identifier);
  }

  if (!replicationData || !replicationData.state) {
    rxdbLog('startOrResyncReplication - replicationData or replicationData.state is not set for identifier', { identifier });
    return false;
  }

  const { state, info, onFinishPull } = replicationData;

  // Check if there are prioritized replications and this replication is not prioritized
  if (prioritizedReplications.size > 0 && !prioritizedReplications.has(identifier)) {
    pausedReplications.add(identifier);
    return false;
  }

  const { promise: onFinishPromise, resolver: onFinishResolver } = getPromiseAndResolver();

  onFinishPull?.subscribe(onFinishResolver);

  rxdbLog('starting or resyncing replication', { identifier, status: info?.status });
  if (info?.status === REPLICATION_INFO_STATUS.COMPLETED) state.reSync();
  else state.start();

  if (waitToFinish) await onFinishPromise;

  return true;
}

/**
 * Resumes all paused replications. This is used when all prioritized replications have been completed.
 */
function resumePausedReplications(): void {
  pausedReplications.forEach(id => {
    startOrResyncReplication(id);
  });
  pausedReplications.clear();
}

/**
 * Handles the completion of a replication.
 * If the replication was prioritized, it will be removed from the prioritized list.
 * Resumes the paused replications if all prioritized replications have been completed.
 * @param {ReplicationIdentifier} identifier - The identifier of the completed replication.
 */
function handlePrioritizedReplicationsFinished(identifier: ReplicationIdentifier): void {
  if (!prioritizedReplications.has(identifier)) return;

  // Remove the replication from the prioritized list
  prioritizedReplications.delete(identifier);

  // Resume paused replications if all prioritized replications have completed
  if (prioritizedReplications.size === 0) {
    resumePausedReplications();
  }
}

/**
 * Prioritizes a replication by identifier.
 * When this replication is completed, it resumes the paused replications, this is handled by handlePrioritizedReplicationsFinished.
 * @param {ReplicationIdentifier[]} identifiers - The identifiers of the replications to prioritize.
 */
export function prioritizeReplications(identifiers: ReplicationIdentifier[]): void {
  // Add the replications identifiers to the prioritized list first
  identifiers.forEach(identifier => prioritizedReplications.add(identifier));

  // Start or resync the prioritized replications
  identifiers.forEach(identifier => startOrResyncReplication(identifier));
}

async function checkUnusedTemplates(templatesIds: string[]): Promise<void> {
  try {
    if (!Array.isArray(templatesIds) || templatesIds.length === 0) return;

    const db = await getNoodleDb();
    const allTemplates = await db.questionnaireTemplate.find().exec();

    const unusedTemplates = allTemplates.filter(template => !templatesIds.includes(template.id));
    const unusedStaleTemplates = unusedTemplates.filter(template => template.status === 'stale');

    await db.questionnaireFieldTemplate
      .find({
        selector: { questionnaireTemplateId: { $in: unusedStaleTemplates.map(template => template.id) } },
      })
      .remove();
  } catch (error) {
    rxdbError('checkUnusedTemplates - Error checking unused templates', error);
  }
}

async function checkUnUsedQuestionnairesResponses(upgradedQuestionnairesIds: string[]): Promise<void> {
  try {
    if (!Array.isArray(upgradedQuestionnairesIds) || upgradedQuestionnairesIds.length === 0) return;

    const db = await getNoodleDb();

    await db.questionnaireResponse.find({ selector: { questionnaireId: { $in: upgradedQuestionnairesIds } } }).remove();

    await db.questionnaireResponse.cleanup(0);
  } catch (error) {
    rxdbError('checkUnUsedQuestionnairesResponses - Error checking unused questionnaires responses', error);
  }
}

function cancelAllReplications(): void {
  Object.values(replicationsData).forEach(replicationData => {
    replicationData?.state?.cancel();
  });
}

async function markLastReplicationDateForIdentifier(identifier: ReplicationIdentifier, markAsDeleted = false): Promise<boolean> {
  try {
    const db = await getNoodleDb();
    const { KEY } = RX_LOCAL_REPLICATION_INFO;

    const localReplicationInfo = await db.getLocal(KEY);
    if (!localReplicationInfo) {
      throw new Error(`Local replication info not found when marking last replication date for identifier ${identifier}`);
    }

    await localReplicationInfo.incrementalPatch({
      [identifier]: markAsDeleted ? null : Date.now(),
    });

    return true;
  } catch (error) {
    rxdbError('markLastReplicationDateForIdentifier - Error marking last replication date for identifier', error);
    return false;
  }
}

async function checkNumberOfQuestionnairesPerCreator(): Promise<void> {
  try {
    await wait(3000);

    const db = await getNoodleDb();
    const localReplicationInfo = await db.getLocal(RX_LOCAL_REPLICATION_INFO.KEY);

    if (!localReplicationInfo) {
      throw new Error('Local replication info not found when checking number of questionnaires per creator');
    }

    const questionnairesResponses: [string, number, string][] = [];
    const questionnairesFieldTemplates: [string, number, string][] = [];
    const identifiersToDelete: string[] = [];

    const localReplicationInfoObject = localReplicationInfo.toJSON()?.data;

    await Promise.all(
      Object.entries(localReplicationInfoObject).map(async ([identifier, value]) => {
        if (!value) return;

        const id = identifier.split('-')[1];
        const replicationData = await getReplicationDataByIdentifier(identifier);

        // If there's a replication already created for this identifier, we don't need to delete it
        // To avoid deleting the data for a replication that is currently running.
        // This shouldn't happen often, but it's possible that a user navigates directly
        // To a questionnaire that would be deleted because it wasn't used for a while.
        if (replicationData) {
          rxdbLog('checkNumberOfQuestionnairesPerCreator - ignoring identifier as a replication is running', { identifier });
          return;
        }

        if (identifier.startsWith(QUESTIONNAIRE_RESPONSES_PREFIX)) {
          questionnairesResponses.push([id, value as number, identifier]);
        } else if (identifier.startsWith(QUESTIONNAIRE_FIELD_TEMPLATES_PREFIX)) {
          questionnairesFieldTemplates.push([id, value as number, identifier]);
        }
      }),
    );

    const maxQuestionnaires = await getMaxQuestionnairesPerCreator();

    if (questionnairesResponses.length > maxQuestionnaires) {
      const questionnairesResponsesToDelete = questionnairesResponses
        .sort((a, b) => a[1] - b[1])
        .slice(0, questionnairesResponses.length - maxQuestionnaires);

      if (questionnairesResponsesToDelete.length > 0) {
        const response = await db.questionnaireResponse
          .find({ selector: { questionnaireId: { $in: questionnairesResponsesToDelete.map(([id]) => id) } } })
          .remove();

        await db.questionnaireResponse.cleanup(0);

        identifiersToDelete.push(...questionnairesResponsesToDelete.map(item => item[2]));

        rxdbLog('checkNumberOfQuestionnairesPerCreator - deleted questionnaires responses', { questionnairesResponsesToDelete, response });
      }
    }

    if (questionnairesFieldTemplates.length > maxQuestionnaires) {
      const questionnairesFieldTemplatesToDelete = questionnairesFieldTemplates
        .sort((a, b) => a[1] - b[1])
        .slice(0, questionnairesFieldTemplates.length - maxQuestionnaires);

      if (questionnairesFieldTemplatesToDelete.length > 0) {
        const response = await db.questionnaireFieldTemplate
          .find({ selector: { questionnaireTemplateId: { $in: questionnairesFieldTemplatesToDelete.map(([id]) => id) } } })
          .remove();

        await db.questionnaireFieldTemplate.cleanup(0);

        identifiersToDelete.push(...questionnairesFieldTemplatesToDelete.map(item => item[2]));

        rxdbLog('checkNumberOfQuestionnairesPerCreator - deleted questionnaires field templates', {
          questionnairesFieldTemplatesToDelete,
          response,
        });
      }
    }

    if (identifiersToDelete.length > 0) {
      await Promise.all(
        identifiersToDelete.map(async identifier => {
          try {
            await markLastReplicationDateForIdentifier(identifier, true);

            const replicationState = await tryToCreateReplication(identifier);
            if (replicationState) {
              await replicationState.start();
              await replicationState.remove();
              removeReplicationData(identifier);
            }
          } catch (error) {
            rxdbError('checkNumberOfQuestionnairesPerCreator - Error deleting replication for identifier', { error, identifier });
          }
        }),
      );
    }
  } catch (error) {
    rxdbError('checkNumberOfQuestionnairesPerCreator - Error checking number of questionnaires per creator', error);
  }
}

export default startReplications;
